import { zodResolver } from '@hookform/resolvers/zod';
import { TypeToConfirmDialog } from '@optools/components/dialog/type-to-confirm-dialog';
import { Button } from '@optools/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@optools/components/ui/form';
import { Input } from '@optools/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@optools/components/ui/radio-group';
import { SheetFooter } from '@optools/components/ui/sheet';
import { cn } from '@optools/components/ui/utils';
import { useAccessCheck } from '@optools/hooks/useAccessCheck';
import { ViewingPolicy } from '@optools/services/operator-gateway/esni/types';
import { selectToolsConfig } from '@optools/store/configApiSlice';
import { useAppSelector } from '@optools/store/hooks';
import { CircleCheck, CircleDashed, CircleX } from 'lucide-react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import { useDetailsParams } from '../hooks/useDetailsParams';
import { AudienceSelect } from './audience-select';
import { ESNIFormPopover } from './esni-form-popover';

export const POPOVER_CONTENT = [
  {
    description:
      'Audiences linked with ALLOW viewing policies will be given access to the media (Entitlements)',
    icon: CircleCheck,
    title: 'ALLOW',
  },
  {
    description:
      'Audiences linked with REJECT viewing policies will be blocked from accessing media (Blackouts)',
    icon: CircleX,
    title: 'REJECT',
  },
  {
    description: 'Audiences linked with IGNORE viewing policies will ignore the policy specified',
    icon: CircleDashed,
    title: 'IGNORE',
  },
];

const formSchema = z.object({
  action: z.string().min(1, { error: 'Action is required' }),
  actionLabel: z.string().min(1, { error: 'Action label is required' }),
  audienceHref: z.string().min(1, { error: 'Audience is required' }),
  href: z.string().min(1, { error: 'Policy href is required' }),
});

export type PolicyFormValues = z.infer<typeof formSchema>;

export enum VIEWING_POLICY_ACTIONS {
  ALLOW = 'ALLOW',
  IGNORE = 'IGNORE',
  REJECT = 'REJECT',
}

const ACTION_RADIO_OPTIONS = [
  VIEWING_POLICY_ACTIONS.ALLOW,
  VIEWING_POLICY_ACTIONS.REJECT,
  VIEWING_POLICY_ACTIONS.IGNORE,
] as const;

export interface PolicyFormProps {
  editMode?: boolean;
  loading: boolean;
  onCancel: () => void;
  onDelete?: (href: string) => void;
  onSubmit: (values: PolicyFormValues) => void;
  onToggleEdit?: () => void;
  viewingPolicy?: ViewingPolicy | undefined;
}

export function PolicyForm({
  editMode = true,
  loading,
  onCancel,
  onDelete,
  onSubmit,
  onToggleEdit,
  viewingPolicy,
}: PolicyFormProps) {
  const { setDetailsParam } = useDetailsParams();
  const {
    action = '',
    actionLabel = '',
    audienceHref = '',
    href = '',
    name = '',
  } = viewingPolicy || {};

  const {
    esni: {
      auth: {
        pages: {
          policies: {
            featureGroups: { deletePolicy },
          },
        },
      },
    },
  } = useAppSelector(selectToolsConfig);
  const { checkGroupAccess } = useAccessCheck();

  const form = useForm<PolicyFormValues>({
    defaultValues: { action, actionLabel, audienceHref, href },
    disabled: loading || !editMode,
    resolver: zodResolver(formSchema),
  });

  const actionValue = form.watch('action');

  return (
    <Form {...form}>
      <form
        className="space-y-6"
        onReset={() => form.reset()}
        onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="href"
          render={({ field }) => {
            const disabled = field.disabled || !!viewingPolicy;
            return (
              <FormItem>
                <FormLabel>Policy Href</FormLabel>
                <FormControl>
                  <Input {...field} disabled={disabled} />
                </FormControl>
                {!!viewingPolicy && editMode && (
                  <FormDescription className="text-xs">Policy href is not editable</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <FormField
          control={form.control}
          name="action"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Action{' '}
                <ESNIFormPopover aria-label="additional action context" content={POPOVER_CONTENT} />
              </FormLabel>
              <FormControl>
                <RadioGroup
                  className={cn({ 'opacity-50': field.disabled }, 'flex flex-wrap gap-4')}
                  disabled={field.disabled}
                  value={field.value}
                  onValueChange={field.onChange}>
                  {ACTION_RADIO_OPTIONS.map((actionOption) => {
                    return (
                      <FormItem
                        key={actionOption}
                        className={cn({
                          hidden: !editMode && field.value !== actionOption,
                        })}>
                        <FormControl>
                          <div className="flex items-center gap-x-2">
                            <RadioGroupItem id={actionOption} value={actionOption} />
                            <FormLabel htmlFor={actionOption}>{actionOption}</FormLabel>
                          </div>
                        </FormControl>
                      </FormItem>
                    );
                  })}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="actionLabel"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {actionValue === VIEWING_POLICY_ACTIONS.IGNORE
                  ? 'Action Label of Ignored Policy'
                  : ' Action Label'}
              </FormLabel>
              <FormControl>
                <Input {...field} disabled={field.disabled} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="audienceHref"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Audience</FormLabel>
              <FormControl>
                {editMode ? (
                  <AudienceSelect
                    disabled={field.disabled}
                    placeHolder="Select an Audience"
                    value={field.value}
                    onValueChange={field.onChange}
                  />
                ) : (
                  <Button
                    className="block"
                    type="button"
                    variant="link"
                    onClick={() => setDetailsParam('audience', field.value)}>
                    {field.value}
                  </Button>
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <SheetFooter>
          {editMode ? (
            <>
              <Button
                disabled={form.formState.disabled}
                type="reset"
                variant="secondary"
                onClick={() => {
                  onCancel();
                  form.reset();
                }}>
                Cancel
              </Button>
              <Button
                disabled={form.formState.disabled}
                loading={form.formState.disabled}
                type="submit">
                Save
              </Button>
            </>
          ) : (
            <>
              <Button disabled={loading} type="button" onClick={() => onToggleEdit?.()}>
                Edit Policy
              </Button>
              {checkGroupAccess(deletePolicy) && onDelete && (
                <TypeToConfirmDialog
                  actionText="Delete"
                  description="Are you sure you want to delete this Policy?"
                  destructive={true}
                  matchInstructions={
                    <p className="text-sm text-muted-foreground">
                      Type <strong className="text-popover-foreground">{name}</strong> to delete
                    </p>
                  }
                  stringToMatch={name}
                  title="Delete Policy"
                  onConfirm={() => onDelete(href)}>
                  <Button disabled={loading} type="button" variant="destructive">
                    Delete Policy
                  </Button>
                </TypeToConfirmDialog>
              )}
            </>
          )}
        </SheetFooter>
      </form>
    </Form>
  );
}
